/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react"
import { Alert } from "react-bootstrap"
import { TApiResponse, TMessage } from "../service/TApiResponse"
import { AxiosError, AxiosResponse } from "axios"
import { stringArrayToComponent } from "../ts/util/commonUtil"

const LocalAlertComponent = (props: { alertSource?: any }) => {
  const [infoList, setInfoList] = useState(new Array<string>())
  const [warnList, setWarnList] = useState(new Array<string>())
  const [errorList, setErrorList] = useState(new Array<string>())

  const toStringList = (arr?: Array<string | TMessage>): string[] => {
    if (!arr) return []
    return arr
      .map((v, _) => {
        if (typeof v === "string") return v
        return (v as TMessage).messageWithCode
      })
      .filter((v) => v)
  }

  const isTApiResponse = (arg: any): arg is TApiResponse<any> => {
    if (!arg) return false
    return (
      arg.infos !== undefined &&
      arg.warns !== undefined &&
      arg.errors !== undefined
    )
  }

  const isAxiosError = (arg: any): arg is AxiosError<any> => {
    if (!arg) return false
    return arg.isAxiosError !== undefined
  }

  const isAxiosResponse = (arg: any): arg is AxiosResponse => {
    if (!arg) return false
    return (
      arg.data !== undefined &&
      arg.status !== undefined &&
      arg.statusText !== undefined &&
      arg.headers !== undefined &&
      arg.config !== undefined
    )
  }

  // メッセージクリア
  const clearMessages = () => {
    setInfoList([])
    setWarnList([])
    setErrorList([])
  }

  /**
   * いずれか(infos, warns, errors)のプロパティを持つオブジェクトのメッセージ出力
   * @returns プロパティ有無
   */
  const showHavingAnyPropsMessage = (o: any): boolean => {
    // 雑に同名プロパティがいたら配列と仮定
    let hasMessageProp = false
    if (o.infos !== undefined) {
      setInfoList(toStringList([...o.infos]))
      hasMessageProp = true
    }
    if (o.warns !== undefined) {
      setWarnList(toStringList([...o.warns]))
      hasMessageProp = true
    }
    if (o.errors !== undefined) {
      setErrorList(toStringList([...o.errors]))
      hasMessageProp = true
    }
    return hasMessageProp
  }

  /**
   * TApiResponseのメッセージ出力
   * @param res TApiResponse
   */
  const showTApiResponseMessage = (res: TApiResponse<any>) => {
    setInfoList([...res.infos.map((e: TMessage) => e.messageWithCode)])
    setWarnList([...res.warns.map((e: TMessage) => e.messageWithCode)])
    setErrorList([...res.errors.map((e: TMessage) => e.messageWithCode)])
  }

  /**
   * AxiosErrorのメッセージ出力
   * @param axiosError
   */
  const showAxiosErrorMessage = (axiosError: AxiosError<any>) => {
    // responseが存在しない場合
    if (axiosError.response == null || axiosError.response.data == null) {
      // AxiosErrorのメッセージを出力
      setInfoList([])
      setWarnList([])
      setErrorList([axiosError.message])
    } else {
      showAxiosResponseMessage(axiosError.response)
    }
  }

  /**
   * AxiosResponseのメッセージ出力
   * @param axiosResponse
   */
  const showAxiosResponseMessage = (axiosResponse: AxiosResponse<any>) => {
    const defaultErrorMessage = `${axiosResponse.status}:${axiosResponse.statusText}`

    if (axiosResponse.data == null) {
      // ResponseのDataが無いとき
      setInfoList([])
      setWarnList([])
      setErrorList([defaultErrorMessage])
    } else {
      // AxiosResponseの中身がTApiResponseの場合
      if (isTApiResponse(axiosResponse.data)) {
        showTApiResponseMessage(axiosResponse.data)
      } else if (axiosResponse.data instanceof Blob) {
        // AxiosResponseの中身がBlobの場合
        const blob = axiosResponse.data

        // Blobのtypeがjsonの場合のみ対応
        if (blob.type.includes("json")) {
          // jsonに非同期でparseしてメッセージ出力
          const blobRes = new Response(blob)
          blobRes
            .json()
            .then((o) => {
              if (isTApiResponse(o)) {
                showTApiResponseMessage(o)
              } else {
                // TApiResponseでないとき
                setInfoList([])
                setWarnList([])
                setErrorList([defaultErrorMessage])
              }
            })
            .catch((err) => {
              showUnknownMessage(err)
            })
        } else {
          // blobのtypeがjsonでないとき
          setInfoList([])
          setWarnList([])
          setErrorList([defaultErrorMessage])
        }
      } else {
        // TApiResponseでもBlobでもない場合は不明扱い
        showUnknownMessage(axiosResponse.data)
      }
    }
  }

  /**
   * 不明エラーのメッセージ出力
   */
  const showUnknownMessage = (unknownSource: any) => {
    console.log(unknownSource)
    setInfoList([])
    setWarnList([])
    setErrorList(["不明なエラー"])
  }

  useEffect(() => {
    // クリア対応
    if (!props.alertSource) {
      clearMessages()
    } else {
      // いずれかのプロパティを持つ場合は出力
      const hasMessageProp = showHavingAnyPropsMessage(props.alertSource)
      if (hasMessageProp) return

      // TApiResponseの場合
      if (isTApiResponse(props.alertSource)) {
        showTApiResponseMessage(props.alertSource)
      } else if (isAxiosError(props.alertSource)) {
        // AxiosErrorの場合
        showAxiosErrorMessage(props.alertSource)
      } else if (isAxiosResponse(props.alertSource)) {
        // ResponseがAxiosResponseの場合
        showAxiosResponseMessage(props.alertSource)
      } else {
        // 不明な場合
        showUnknownMessage(props.alertSource)
      }
    }
  }, [props.alertSource])

  return (
    <>
      {(warnList.length > 0 || infoList.length > 0 || errorList.length > 0) && (
        <>
          {errorList.length > 0 && (
            <Alert className="w-100per" variant={"danger"}>
              {stringArrayToComponent(errorList)}
            </Alert>
          )}
          {warnList.length > 0 && (
            <Alert className="w-100per" variant={"warning"}>
              {stringArrayToComponent(warnList)}
            </Alert>
          )}
          {infoList.length > 0 && (
            <Alert className="w-100per" variant={"info"}>
              {stringArrayToComponent(infoList)}
            </Alert>
          )}
        </>
      )}
    </>
  )
}

export default LocalAlertComponent
